// apps/web/src/components/TemplateDeploymentModal.js
import React, { useState } from 'react';
import { useToast } from './Toast';
import { processTemplate, validateVariable, getDefaultValues } from '../services/templateEngine';

/**
 * TemplateDeploymentModal - Interactive form for applying templates
 * Shows form fields based on template variables, validates input,
 * processes template with template engine, and creates tasks
 */
export default function TemplateDeploymentModal({ template, listId, onClose, onTasksCreated }) {
  const toast = useToast();

  // Extract variables and tasks from template.fields if it's an interactive template
  // Ensure they are always arrays
  const variables = Array.isArray(template.fields?.variables)
    ? template.fields.variables
    : Array.isArray(template.variables)
    ? template.variables
    : [];

  const tasks = Array.isArray(template.fields?.tasks)
    ? template.fields.tasks
    : Array.isArray(template.tasks)
    ? template.tasks
    : [];

  const [values, setValues] = useState(getDefaultValues(variables));
  const [errors, setErrors] = useState({});
  const [deploying, setDeploying] = useState(false);

  const isInteractive = variables.length > 0;

  function handleChange(variableId, value) {
    setValues(prev => ({ ...prev, [variableId]: value }));
    // Clear error when user starts typing
    if (errors[variableId]) {
      setErrors(prev => ({ ...prev, [variableId]: null }));
    }
  }

  function handleCheckboxGroupChange(variableId, option, checked) {
    setValues(prev => {
      // Ensure current value is always an array
      const currentValue = prev[variableId];
      const current = Array.isArray(currentValue) ? currentValue : [];

      if (checked) {
        return { ...prev, [variableId]: [...current, option] };
      } else {
        return { ...prev, [variableId]: current.filter(v => v !== option) };
      }
    });
  }

  async function handleDeploy() {
    // Validate all inputs
    const newErrors = {};
    for (const variable of variables) {
      const validation = validateVariable(variable, values[variable.id]);
      if (!validation.valid) {
        newErrors[variable.id] = validation.error;
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error('Please fix the errors before deploying');
      return;
    }

    setDeploying(true);

    try {
      let generatedTasks = [];

      // Check if this is a static HTML template (no tasks, just HTML)
      if (tasks.length === 0 && template.html) {
        // For static templates, create a single task with the HTML content
        generatedTasks = [{
          title: template.title,
          description: template.html,
          status: 'todo',
        }];
      } else {
        // Build template structure for processing
        const templateToProcess = {
          ...template,
          variables: variables,
          tasks: tasks
        };

        // Process template with template engine
        generatedTasks = processTemplate(templateToProcess, values);

        if (generatedTasks.length === 0) {
          toast.error('No tasks generated. Check your template configuration.');
          setDeploying(false);
          return;
        }
      }

      // Create tasks via API
      const token = localStorage.getItem('accessToken');
      const createdTasks = [];

      for (const taskData of generatedTasks) {
        const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1'}/tasks/${listId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            title: taskData.title,
            description: taskData.description || '',
            status: taskData.status || 'todo',
          }),
        });

        if (!res.ok) {
          throw new Error(`Failed to create task: ${taskData.title}`);
        }

        const result = await res.json();
        createdTasks.push(result.task);
      }

      // Track template deployment (fire and forget, don't block UI)
      fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1'}/templates/${template.id}/track-deployment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      }).catch(err => console.warn('Failed to track deployment:', err));

      toast.success(`Successfully created ${createdTasks.length} tasks from template`);

      if (onTasksCreated) {
        onTasksCreated(createdTasks);
      }

      onClose();
    } catch (error) {
      console.error('Error deploying template:', error);
      toast.error(error.message || 'Failed to deploy template');
      setDeploying(false);
    }
  }

  function renderField(variable) {
    const value = values[variable.id];
    const error = errors[variable.id];

    switch (variable.type) {
      case 'text':
        return (
          <div key={variable.id} className="TemplateDeployField">
            <label className="TemplateDeployLabel">
              {variable.label}
              {variable.required && <span className="TemplateDeployRequired"> *</span>}
            </label>
            {variable.description && (
              <p className="TemplateDeployHint">{variable.description}</p>
            )}
            <input
              type="text"
              className={`TemplateDeployInput ${error ? 'error' : ''}`}
              value={value || ''}
              onChange={(e) => handleChange(variable.id, e.target.value)}
              placeholder={variable.placeholder || ''}
            />
            {error && <span className="TemplateDeployError">{error}</span>}
          </div>
        );

      case 'number':
        return (
          <div key={variable.id} className="TemplateDeployField">
            <label className="TemplateDeployLabel">
              {variable.label}
              {variable.required && <span className="TemplateDeployRequired"> *</span>}
            </label>
            {variable.description && (
              <p className="TemplateDeployHint">{variable.description}</p>
            )}
            <input
              type="number"
              className={`TemplateDeployInput ${error ? 'error' : ''}`}
              value={value || ''}
              onChange={(e) => handleChange(variable.id, parseFloat(e.target.value) || 0)}
              min={variable.min}
              max={variable.max}
              placeholder={variable.placeholder || ''}
            />
            {error && <span className="TemplateDeployError">{error}</span>}
          </div>
        );

      case 'date':
        return (
          <div key={variable.id} className="TemplateDeployField">
            <label className="TemplateDeployLabel">
              {variable.label}
              {variable.required && <span className="TemplateDeployRequired"> *</span>}
            </label>
            {variable.description && (
              <p className="TemplateDeployHint">{variable.description}</p>
            )}
            <input
              type="date"
              className={`TemplateDeployInput ${error ? 'error' : ''}`}
              value={value || ''}
              onChange={(e) => handleChange(variable.id, e.target.value)}
            />
            {error && <span className="TemplateDeployError">{error}</span>}
          </div>
        );

      case 'dropdown':
        return (
          <div key={variable.id} className="TemplateDeployField">
            <label className="TemplateDeployLabel">
              {variable.label}
              {variable.required && <span className="TemplateDeployRequired"> *</span>}
            </label>
            {variable.description && (
              <p className="TemplateDeployHint">{variable.description}</p>
            )}
            <select
              className={`TemplateDeploySelect ${error ? 'error' : ''}`}
              value={value || ''}
              onChange={(e) => handleChange(variable.id, e.target.value)}
            >
              <option value="">Select...</option>
              {(variable.options || []).map((option, idx) => (
                <option key={idx} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {error && <span className="TemplateDeployError">{error}</span>}
          </div>
        );

      case 'checkbox':
        return (
          <div key={variable.id} className="TemplateDeployField">
            <label className="TemplateDeployCheckbox">
              <input
                type="checkbox"
                checked={value || false}
                onChange={(e) => handleChange(variable.id, e.target.checked)}
              />
              <span>{variable.label}</span>
            </label>
            {variable.description && (
              <p className="TemplateDeployHint">{variable.description}</p>
            )}
          </div>
        );

      case 'checkbox_group':
        // Ensure value is always an array
        const checkboxGroupValue = Array.isArray(value) ? value : [];
        return (
          <div key={variable.id} className="TemplateDeployField">
            <label className="TemplateDeployLabel">
              {variable.label}
              {variable.required && <span className="TemplateDeployRequired"> *</span>}
            </label>
            {variable.description && (
              <p className="TemplateDeployHint">{variable.description}</p>
            )}
            <div className="TemplateDeployCheckboxGroup">
              {(variable.options || []).map((option, idx) => (
                <label key={idx} className="TemplateDeployCheckbox">
                  <input
                    type="checkbox"
                    checked={checkboxGroupValue.includes(option)}
                    onChange={(e) => handleCheckboxGroupChange(variable.id, option, e.target.checked)}
                  />
                  <span>{option}</span>
                </label>
              ))}
            </div>
            {error && <span className="TemplateDeployError">{error}</span>}
          </div>
        );

      default:
        return null;
    }
  }

  return (
    <div className="ModalOverlay" onClick={onClose}>
      <div className="TaskEditPanel TemplateDeployModal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="TaskEditHeader">
          <div className="TaskEditHeaderLeft">
            <div className="TaskEditHeading">Apply Template: {template.title}</div>
            {template.summary && (
              <div className="TaskEditSub">{template.summary}</div>
            )}
          </div>
          <div className="TaskEditHeaderRight">
            <button className="Btn" onClick={onClose}>Close</button>
          </div>
        </div>

        {/* Body */}
        <div className="TaskEditBody" style={{ marginTop: 16 }}>
          {isInteractive ? (
            <>
              <div className="TemplateDeployIntro">
                <p>Fill in the fields below to customise this template:</p>
              </div>

              <div className="TemplateDeployForm">
                {variables.map(renderField)}
              </div>
            </>
          ) : (
            <div className="TemplateDeployStatic">
              {tasks.length > 0 ? (
                <p>This template will create {tasks.length} task{tasks.length !== 1 ? 's' : ''}.</p>
              ) : template.html ? (
                <p>This template will create 1 task with the template content as a reference checklist.</p>
              ) : (
                <p>This template will create {tasks.length || 0} tasks.</p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="TaskEditActions" style={{ marginTop: 24, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="Btn BtnSecondary" onClick={onClose} disabled={deploying}>
            Cancel
          </button>
          <button className="Btn BtnPrimary" onClick={handleDeploy} disabled={deploying}>
            {deploying ? 'Deploying...' : `Apply Template`}
          </button>
        </div>
      </div>
    </div>
  );
}
